import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styles from "./TeamInformation.module.css";
import Subscription from "../Subscription/Subscription";
import MatchesAPI from "../../data/matches";
import PlayersAPI from "../../data/players";

import {
  FaArrowLeft,
  FaUsers,
  FaTrophy,
  FaCalendarAlt,
  FaFilter,
  FaInfoCircle,
  FaLandmark,
  FaUserTag,
  FaChartBar,
  FaChartPie,
  FaClock,
  FaMapMarkerAlt,
  FaArrowRight,
  FaHome,
  FaUserFriends,
  FaRedo,
  FaGamepad,
  FaUser,
  FaIdBadge,
  FaUserCog,
  FaSearch,
  FaSort,
  FaSortAmountDown,
  FaSortAmountUp,
  FaForward,
  FaHistory,
  FaList,
  FaTimes,
  FaChartLine,

} from "react-icons/fa";
import { GlobalContext } from "../../constants/Context";
import { useTeamInformationState } from "../../states/useTeamInformationState";
import CustomDropdown from "../../components/Input/CustomDropdown";

const TeamInformation = () => {
  const { SITE_ID, CLUB_ID } = useContext(GlobalContext);
  const { teamId } = useParams();
  const navigate = useNavigate();

  const { teamData, setTeamData, allMatches, setAllMatches, teamMatches, setTeamMatches, filteredMatches, setFilteredMatches, players, setPlayers, filteredPlayers, setFilteredPlayers, loading, setLoading, error, setError, activeTab, setActiveTab, yearsList, setYearsList, currentSeason, setCurrentSeason, matchFilter, setMatchFilter, playerSearch, setPlayerSearch, sortOrder, setSortOrder, matchSearchTerm, setMatchSearchTerm, matchesApiKey, setMatchesApiKey, playersApiKey, setPlayersApiKey } = useTeamInformationState();

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2000;
    const endYear = currentYear + 5;

    const years = [];
    for (let year = endYear; year >= startYear; year--) {
      years.push({
        value: year.toString(),
        label: year.toString(),
      });
    }
    setYearsList(years);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const determineMatchStatus = (matchDate, originalStatus) => {
    const matchStatus = (originalStatus || "").toLowerCase();

    if (matchStatus.includes("abandoned") || matchStatus.includes("cancelled")) {
      return "cancelled";
    }

    if (matchStatus.includes("live") || matchStatus.includes("in progress")) {
      return "live";
    }

    if (matchStatus.includes("completed") || matchStatus.includes("result")) {
      return "completed";
    }

    try {
      const [day, month, year] = matchDate.split('/');
      const matchDateObj = new Date(year, month - 1, day);
      const today = new Date();

      matchDateObj.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (matchDateObj < today) {
        return "completed";
      } else if (matchDateObj > today) {
        return "upcoming";
      } else {
        return "live";
      }
    } catch (error) {
      console.error("Error parsing date:", matchDate, error);

      if (currentSeason < new Date().getFullYear().toString()) {
        return "completed";
      }
      return "upcoming";
    }
  };

  // Format match date for display (same as LeagueTable)
  const formatMatchDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const [day, month, year] = dateString.split('/');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day} ${months[parseInt(month) - 1]} ${year}`;
    } catch (err) {
      return dateString;
    }
  };

  const processMatchesData = (matches) => {
    return matches.map(match => {
      const status = determineMatchStatus(match.match_date, match.status);
      const isCurrentTeam = match.home_team_id === teamId;

      return {
        id: match.id,
        match_date: match.match_date,
        match_time: match.match_time || "Not Decided",
        status: status,
        venue: match.ground_name || "Not Decided",
        home_team_id: match.home_team_id,
        home_team_name: match.home_team_name,
        home_club_name: match.home_club_name,
        away_team_id: match.away_team_id,
        away_team_name: match.away_team_name,
        away_club_name: match.away_club_name,
        competition_name: match.competition_name || "Competition",
        match_type: match.match_type,
        no_of_overs: match.no_of_overs,
        umpires: [match.umpire_1_name, match.umpire_2_name, match.umpire_3_name].filter(Boolean),
        referee: match.referee_name,
        scorers: [match.scorer_1_name, match.scorer_2_name].filter(Boolean),
        is_current_team_home: isCurrentTeam,
        opponent_club: isCurrentTeam ? match.away_club_name : match.home_club_name,
        opponent_team: isCurrentTeam ? match.away_team_name : match.home_team_name
      };
    });
  };

  const filterMatches = (matches, filter) => {
    if (filter === "all") return matches;
    return matches.filter(match => match.status === filter);
  };

  const getMatchStats = (matches) => {
    return {
      total: matches.length,
      upcoming: matches.filter(m => m.status === "upcoming").length,
      live: matches.filter(m => m.status === "live").length,
      completed: matches.filter(m => m.status === "completed").length,
      cancelled: matches.filter(m => m.status === "cancelled").length
    };
  };

  // Handle matches data from API component
  const handleMatchesData = (data) => {
    if (!data || data.length === 0) {
      setError(`No matches found for ${currentSeason} season`);
      setAllMatches([]);
      setTeamMatches([]);
      setFilteredMatches([]);
      setLoading(false);
      return;
    }

    setAllMatches(data);

    const teamMatchesData = data.filter(match =>
      match.home_team_id === teamId || match.away_team_id === teamId
    );

    if (teamMatchesData.length === 0) {
      setError(`No matches found for this team in ${currentSeason}`);
      setTeamMatches([]);
      setFilteredMatches([]);
      setLoading(false);
      return;
    }

    const processedMatches = processMatchesData(teamMatchesData);
    setTeamMatches(processedMatches);

    const initialFiltered = filterMatches(processedMatches, matchFilter);
    setFilteredMatches(initialFiltered);

    if (teamMatchesData.length > 0) {
      const firstMatch = teamMatchesData[0];
      const isHomeTeam = firstMatch.home_team_id === teamId;

      const teamInfo = {
        team_name: isHomeTeam ? firstMatch.home_team_name : firstMatch.away_team_name,
        club_name: isHomeTeam ? firstMatch.home_club_name : firstMatch.away_club_name,
        club_id: isHomeTeam ? firstMatch.home_club_id : firstMatch.away_club_id,
        team_id: teamId,
        league_name: firstMatch.league_name,
        competition_name: firstMatch.competition_name,
        season: currentSeason
      };

      setTeamData(teamInfo);
    }

    setError(null);
    setLoading(false);
  };

  // Handle matches API error
  const handleMatchesAPIError = (errorMsg) => {
    console.error("Matches API Error:", errorMsg);
    setError(`Failed to load matches: ${errorMsg}`);
    setLoading(false);
  };

  // Handle players data from API
  const handlePlayersData = (playersData) => {
    if (playersData && playersData.length > 0) {
      const sortedPlayers = [...playersData].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setPlayers(sortedPlayers);
      setFilteredPlayers(sortedPlayers);
    }
  };

  // Handle players API error
  const handlePlayersAPIError = (errorMsg) => {
    console.error("Players API Error:", errorMsg);
    // Don't set overall error for players
  };

  // Filter and sort players based on search and sort order
  useEffect(() => {
    let result = [...players];

    // Apply search filter
    if (playerSearch) {
      result = result.filter(player =>
        player.name.toLowerCase().includes(playerSearch.toLowerCase())
      );
    }

    // Apply sorting
    if (sortOrder === "asc") {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      result.sort((a, b) => b.name.localeCompare(a.name));
    }

    setFilteredPlayers(result);
  }, [players, playerSearch, sortOrder]);

  // Update filtered matches when filter or search changes
  useEffect(() => {
    if (teamMatches.length > 0) {
      let filtered = filterMatches(teamMatches, matchFilter);

      // Apply search filter if exists
      if (matchSearchTerm.trim()) {
        filtered = filtered.filter(match => {
          const searchableText = [
            match.home_club_name,
            match.home_team_name,
            match.away_club_name,
            match.away_team_name,
            match.venue,
            match.competition_name
          ].join(' ').toLowerCase();

          return searchableText.includes(matchSearchTerm.toLowerCase());
        });
      }

      setFilteredMatches(filtered);
    }
  }, [matchFilter, teamMatches, matchSearchTerm]);

  // Handle season change
  const handleSeasonChange = (season) => {
    if (season === currentSeason) return;

    setCurrentSeason(season);
    // Reset states for new season
    setTeamData(null);
    setAllMatches([]);
    setTeamMatches([]);
    setFilteredMatches([]);
    setPlayers([]);
    setFilteredPlayers([]);
    setMatchFilter("all");
    setMatchSearchTerm("");
    setPlayerSearch("");
    setLoading(true);
    setError(null);

    // Force re-fetch of data for new season
    setMatchesApiKey(prevKey => prevKey + 1);
    setPlayersApiKey(prevKey => prevKey + 1);
  };

  const yearOptions = useMemo(() =>
    yearsList?.map(year => ({
      value: year.value,
      label: year.label
    })) || [], [yearsList]
  );

  const getPlayerStats = () => {
    const total = players.length;
    return {
      total,
      batsmen: Math.floor(total * 0.4),
      bowlers: Math.floor(total * 0.35),
      allRounders: Math.floor(total * 0.25)
    };
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Clear search functions
  const clearMatchSearch = () => {
    setMatchSearchTerm("");
  };

  const clearPlayerSearch = () => {
    setPlayerSearch("");
  };

  // Get match statistics
  const matchStats = getMatchStats(teamMatches);
  const playerStats = getPlayerStats();

  // Render match card based on actual API data
  const renderMatchCard = (match) => {
    const {
      id,
      match_date,
      match_time,
      home_team_name,
      away_team_name,
      home_club_name,
      away_club_name,
      competition_name,
      match_type,
      no_of_overs,
      venue,
      status,
      umpires,
      referee,
      scorers
    } = match;

    const isHomeTeam = match.home_team_id === teamId;
    const isCompleted = status === "completed";
    const isLive = status === "live";
    const isUpcoming = status === "upcoming";

    return (
      <Link to={`/cricket-match/${id}`} style={{ textDecoration: "none" }} key={id}>
        <div className={`${styles.matchCard} ${isCompleted ? styles.completedMatch :
          isLive ? styles.liveMatch :
            styles.upcomingMatch
          }`}>
          <div className={styles.matchHeader}>
            <div className={styles.matchDateContainer}>
              <FaCalendarAlt />
              <span className={styles.matchDate}>
                {formatMatchDate(match_date)}
              </span>
              {match_time && match_time !== "Not Decided" && (
                <span className={styles.matchTime}>
                  | {match_time}
                </span>
              )}
            </div>
            <div className={styles.matchFormat}>
              <FaClock />
              <span>{match_type}</span>
            </div>
          </div>



          {venue && venue !== "Not Decided" && (
            <div className={styles.venueInfo}>
              <FaMapMarkerAlt />
              <span>{venue}</span>
            </div>
          )}

          <div className={styles.teamsContainer}>
            {/* Home Team */}
            <div className={styles.teamRow}>
              <div className={styles.teamInfo}>
                <div className={styles.teamNameWrapper}>
                  <span className={styles.teamName}>
                    {home_club_name} - {home_team_name}
                  </span>
                  {isHomeTeam && <span className={styles.currentTeamBadge}>(Your Team)</span>}
                </div>
              </div>
            </div>

            <div className={styles.vsSeparator}>
              <span className={styles.vsSpan}>VS</span>
            </div>

            {/* Away Team */}
            <div className={styles.teamRow}>
              <div className={styles.teamInfo}>
                <div className={styles.teamNameWrapper}>
                  <span className={styles.teamName}>
                    {away_club_name} - {away_team_name}
                  </span>
                  {!isHomeTeam && <span className={styles.currentTeamBadge}>(Your Team)</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Match Officials */}
          <div className={styles.matchOfficials}>

            {referee && (
              <div className={styles.officialRow}>
                <span className={styles.officialLabel}>Referee:</span>
                <span className={styles.officialNames}>{referee}</span>
              </div>
            )}
            {scorers.length > 0 && (
              <div className={styles.officialRow}>
                <span className={styles.officialLabel}>Scorers:</span>
                <span className={styles.officialNames}>{scorers.join(', ')}</span>
              </div>
            )}
          </div>

          <div className={styles.matchFooter}>
            <div className={styles.matchCompetition}>
              <FaTrophy />
              <span>{competition_name}</span>
            </div>
            <div className={styles.matchStatusBadge}>
              {isLive ? (
                <>
                  <FaChartLine style={{ color: "#dc2626" }} />
                  <span style={{ color: "#dc2626", fontWeight: "bold" }}>Live Now</span>
                </>
              ) : isCompleted ? (
                <>
                  <FaHistory />
                  <span>Match Completed</span>
                </>
              ) : (
                <>
                  <FaForward />
                  <span>Upcoming Match</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  // Render loading state
  if (loading) {
    return (
      <div className={styles.teamPage}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>

        {/* API Components with keys */}
        <MatchesAPI
          key={`matches-${teamId}-${currentSeason}-${matchesApiKey}`}
          siteId={SITE_ID}
          season={currentSeason}
          onDataLoaded={handleMatchesData}
          onError={handleMatchesAPIError}
          onLoading={() => { }}
        />
        <PlayersAPI
          key={`players-${CLUB_ID}-${currentSeason}-${playersApiKey}`}
          clubId={CLUB_ID}
          onDataLoaded={handlePlayersData}
          onError={handlePlayersAPIError}
          onLoading={() => { }}
        />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={styles.teamPage}>
        <section className={styles.headerSection}>
          <div className={styles.container}>
            <div className={styles.headerContent}>
              <h1 className={styles.heroTitle}>
                <span>Royal Sports League</span>
              </h1>

            </div>
          </div>
        </section>

        <div className={styles.noDataWrapper}>
          <div className={styles.noData}>
            <div className={styles.noDataIcon}><FaInfoCircle /></div>
            <h3>No Team Information Found</h3>
          </div>
        </div>

        {/* API Components */}
        <MatchesAPI
          key={`matches-${teamId}-${currentSeason}-${matchesApiKey}`}
          siteId={SITE_ID}
          season={currentSeason}
          onDataLoaded={handleMatchesData}
          onError={handleMatchesAPIError}
          onLoading={() => { }}
        />
        <PlayersAPI
          key={`players-${CLUB_ID}-${currentSeason}-${playersApiKey}`}
          clubId={SITE_ID}
          onDataLoaded={handlePlayersData}
          onError={handlePlayersAPIError}
          onLoading={() => { }}
        />
      </div>
    );
  }

  // Main render with data
  return (
    <div className={styles.teamPage}>
      {/* API Components with dynamic keys */}
      <MatchesAPI
        key={`matches-${teamId}-${currentSeason}-${matchesApiKey}`}
        siteId={SITE_ID}
        season={currentSeason}
        onDataLoaded={handleMatchesData}
        onError={handleMatchesAPIError}
        onLoading={() => { }}
      />
      <PlayersAPI
        key={`players-${CLUB_ID}-${currentSeason}-${playersApiKey}`}
        clubId={SITE_ID}
        onDataLoaded={handlePlayersData}
        onError={handlePlayersAPIError}
        onLoading={() => { }}
      />

      {/* Header Section */}
      <section className={styles.headerSection}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <button className={styles.backButtonHeader} onClick={handleBack}>
              <FaArrowLeft />
              Back
            </button>

            <div className={styles.teamHeader}>
              <div className={styles.teamLogo}>
                <FaUsers />
              </div>
              <div className={styles.teamTitle}>
                <h1 className={styles.heroTitle}>
                  {teamData?.team_name || "Team"}
                </h1>
                <p className={styles.clubName}>
                  {teamData?.club_name || "Club Name"} • {currentSeason} Season
                </p>
              </div>
            </div>

            <div className={styles.teamMeta}>
              <div className={styles.metaItem}>
                <FaTrophy />
                <span>{teamData?.competition_name}</span>
              </div>
              <div className={styles.metaItem}>
                <FaCalendarAlt />
                <span>Season {teamData?.season || currentSeason}</span>
              </div>
              <div className={styles.metaItem}>
                <FaUsers />
                <span>{players.length} Players</span>
              </div>
              <div className={styles.metaItem}>
                <FaCalendarAlt />
                <span>{teamMatches.length} Matches</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Controls Section */}
      <section className={styles.controlsSection}>
        <div className={styles.container}>
          <div className={styles.controlsGrid}>
            {/* Season Selector - LeagueList style */}
            <div className={styles.seasonSelectorContainer}>
              <div className={styles.seasonSelector}>
                <label htmlFor="seasonSelect">
                  <FaFilter />
                  Season:
                </label>
                <div className={styles.selectWrapper}>
                  <CustomDropdown
                    options={yearOptions}
                    value={currentSeason}
                    onChange={(value) => handleSeasonChange(value)}
                  />
                  
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className={styles.tabsContainer}>
              <button
                className={`${styles.tabButton} ${activeTab === "info" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("info")}
              >
                <FaInfoCircle />
                Team Info
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "matches" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("matches")}
              >
                <FaCalendarAlt />
                Matches ({teamMatches.length})
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "players" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("players")}
              >
                <FaUsers />
                Players ({players.length})
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          {activeTab === "info" ? (
            // Team Info Tab
            <div className={styles.infoTab}>
              <div className={styles.infoCard}>
                <div className={styles.cardHeader}>
                  <h3>
                    <FaInfoCircle />
                    Team Information - {currentSeason}
                  </h3>
                </div>

                <div className={styles.cardBody}>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <div className={styles.infoLabel}>
                        <FaUserTag />
                        Team Name
                      </div>
                      <div className={styles.infoValue}>
                        {teamData?.team_name || "N/A"}
                      </div>
                    </div>

                    <div className={styles.infoItem}>
                      <div className={styles.infoLabel}>
                        <FaLandmark />
                        Club
                      </div>
                      <div className={styles.infoValue}>
                        {teamData?.club_name || "N/A"}
                      </div>
                    </div>

                    <div className={styles.infoItem}>
                      <div className={styles.infoLabel}>
                        <FaTrophy />
                        Competition
                      </div>
                      <div className={styles.infoValue}>
                        {teamData?.competition_name || "N/A"}
                      </div>
                    </div>

                    <div className={styles.infoItem}>
                      <div className={styles.infoLabel}>
                        <FaCalendarAlt />
                        Season
                      </div>
                      <div className={styles.infoValue}>
                        {teamData?.season || currentSeason}
                      </div>
                    </div>
                  </div>

                  <div className={styles.statsSummary}>
                    <h4>
                      <FaChartBar />
                      {currentSeason} Season Summary
                    </h4>

                    <div className={styles.statsGrid}>
                      <div className={styles.statCard}>
                        <div className={styles.statValue}>
                          {matchStats.total}
                        </div>
                        <div className={styles.statLabel}>
                          Total Matches
                        </div>
                      </div>

                      <div className={styles.statCard}>
                        <div className={styles.statValue}>
                          {matchStats.completed}
                        </div>
                        <div className={styles.statLabel}>
                          Completed
                        </div>
                      </div>

                      <div className={styles.statCard}>
                        <div className={styles.statValue}>
                          {matchStats.upcoming}
                        </div>
                        <div className={styles.statLabel}>
                          Upcoming
                        </div>
                      </div>

                      <div className={styles.statCard}>
                        <div className={styles.statValue}>
                          {matchStats.live}
                        </div>
                        <div className={styles.statLabel}>
                          Live
                        </div>
                      </div>

                      {matchStats.cancelled > 0 && (
                        <div className={styles.statCard}>
                          <div className={styles.statValue}>
                            {matchStats.cancelled}
                          </div>
                          <div className={styles.statLabel}>
                            Cancelled
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === "players" ? (
            // Players Tab
            <div className={styles.playersTab}>
              <div className={styles.playersContainer}>
                <div className={styles.sectionHeader}>
                  <h3>
                    <FaUsers />
                    Team Players - {currentSeason}
                    <span className={styles.playerCount}>
                      ({filteredPlayers.length} of {players.length} players)
                    </span>
                  </h3>
                </div>

                {/* Players Search Box */}
                <div className={styles.searchWrapper}>
                  <div className={styles.searchBox}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Search players by name..."
                      value={playerSearch}
                      onChange={(e) => setPlayerSearch(e.target.value)}
                      className={styles.searchInput}
                      aria-label="Search players"
                    />
                    {playerSearch && (
                      <button
                        className={styles.searchClear}
                        onClick={clearPlayerSearch}
                        aria-label="Clear search"
                        type="button"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>

                  <button
                    className={styles.sortButton}
                    onClick={toggleSortOrder}
                  >
                    {sortOrder === "asc" ? <FaSortAmountUp /> : <FaSortAmountDown />}
                    Sort {sortOrder === "asc" ? "A-Z" : "Z-A"}
                  </button>
                </div>

                {filteredPlayers.length === 0 ? (
                  <div className={styles.noPlayers}>
                    <div className={styles.noPlayersIcon}>
                      <FaUsers />
                    </div>
                    <h4>No players found</h4>
                    <p>
                      {playerSearch
                        ? `No players found matching "${playerSearch}" for ${currentSeason}`
                        : `No player data available for this club in ${currentSeason}`}
                    </p>
                    {playerSearch && (
                      <button
                        className={styles.clearSearchButton}
                        onClick={clearPlayerSearch}
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={styles.playersGrid}>
                    {filteredPlayers.map((player, index) => (
                      <div key={player.member_id || index} className={styles.playerCard}>
                        <div className={styles.playerHeader}>
                          <div className={styles.playerAvatar}>
                            <FaUser />
                            <span className={styles.playerNumber}>
                              {index + 1}
                            </span>
                          </div>
                          <div className={styles.playerInfo}>
                            <h4 className={styles.playerName}>
                              {player.name}
                            </h4>
                            <div className={styles.playerId}>
                              <FaIdBadge />
                              ID: {player.member_id}
                            </div>
                          </div>
                        </div>

                        <div className={styles.playerDetails}>
                          <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>Status:</span>
                            <span className={`${styles.detailValue} ${styles.activeStatus}`}>
                              Active
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles.playersSummary}>
                  <div className={styles.summaryCard}>
                    <h4>
                      <FaChartPie />
                      Squad Overview - {currentSeason}
                    </h4>
                    <div className={styles.summaryStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statNumber}>
                          {players.length}
                        </span>
                        <span className={styles.statLabel}> Total Players</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statNumber}>
                          {playerStats.batsmen}
                        </span>
                        <span className={styles.statLabel}> Batsmen</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statNumber}>
                          {playerStats.bowlers}
                        </span>
                        <span className={styles.statLabel}> Bowlers</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statNumber}>
                          {playerStats.allRounders}
                        </span>
                        <span className={styles.statLabel}> All-rounders</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Matches Tab
            <div className={styles.matchesTab}>
              <div className={styles.matchesContainer}>
                <h2 color="red
                  ">
                  <FaCalendarAlt />
                  Match Schedule - {currentSeason}
                  <span className={styles.matchCount}>
                    {" "}({filteredMatches.length} matches)
                  </span>
                </h2>
                <div className={styles.sectionHeader}>

                  {/* Match Search Box */}
                  <div className={styles.searchWrapper}>
                    <div className={styles.searchBox}>
                      <FaSearch className={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder="Search matches by team, ground..."
                        value={matchSearchTerm}
                        onChange={(e) => setMatchSearchTerm(e.target.value)}
                        className={styles.searchInput}
                        aria-label="Search matches"
                      />
                      {matchSearchTerm && (
                        <button
                          className={styles.searchClear}
                          onClick={clearMatchSearch}
                          aria-label="Clear search"
                          type="button"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Match Filter Tabs */}
                  <div className={styles.filterTabs}>
                    <button
                      className={`${styles.filterTab} ${matchFilter === "all" ? styles.activeFilter : ""}`}
                      onClick={() => setMatchFilter("all")}
                    >
                      All ({matchStats.total})
                    </button>
                    <button
                      className={`${styles.filterTab} ${matchFilter === "completed" ? styles.activeFilter : ""}`}
                      onClick={() => setMatchFilter("completed")}
                    >
                      <FaHistory />
                      Completed ({matchStats.completed})
                    </button>
                    <button
                      className={`${styles.filterTab} ${matchFilter === "upcoming" ? styles.activeFilter : ""}`}
                      onClick={() => setMatchFilter("upcoming")}
                    >
                      <FaForward />
                      Upcoming ({matchStats.upcoming})
                    </button>
                    <button
                      className={`${styles.filterTab} ${matchFilter === "live" ? styles.activeFilter : ""}`}
                      onClick={() => setMatchFilter("live")}
                    >
                      <FaChartLine />
                      Live ({matchStats.live})
                    </button>
                  </div>
                </div>

                {filteredMatches.length === 0 ? (
                  <div className={styles.noMatches}>
                    <div className={styles.noMatchesIcon}>
                      {matchFilter === "live" ? <FaChartLine /> :
                        matchFilter === "completed" ? <FaHistory /> :
                          matchFilter === "upcoming" ? <FaForward /> :
                            <FaCalendarAlt />}
                    </div>
                    <h4>
                      {matchSearchTerm
                        ? `No matches found matching "${matchSearchTerm}"`
                        : `No ${matchFilter === "all" ? "" : matchFilter + " "}matches found`}
                    </h4>
                    <p>
                      {matchSearchTerm
                        ? `Try a different search term or clear the search`
                        : `Try selecting a different filter or season`}
                    </p>
                    {matchSearchTerm && (
                      <button
                        className={styles.clearSearchButton}
                        onClick={clearMatchSearch}
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={styles.matchesGrid}>
                    {filteredMatches.map((match) => renderMatchCard(match))}
                  </div>
                )}

                <div className={styles.matchesSummary}>
                  <div className={styles.summaryCard}>
                    <h4>
                      <FaChartPie />
                      Matches Breakdown - {currentSeason}
                    </h4>
                    <div className={styles.summaryStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statNumber}>
                          {matchStats.completed}
                        </span>
                        <span className={styles.statLabel}>Completed</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statNumber}>
                          {matchStats.live}
                        </span>
                        <span className={styles.statLabel}>Live</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statNumber}>
                          {matchStats.upcoming}
                        </span>
                        <span className={styles.statLabel}>Upcoming</span>
                      </div>
                      {matchStats.cancelled > 0 && (
                        <div className={styles.statItem}>
                          <span className={styles.statNumber}>
                            {matchStats.cancelled}
                          </span>
                          <span className={styles.statLabel}>Cancelled</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Subscription />
    </div>
  );
};

export default TeamInformation; 